/**
 * Backend-agnostic API client
 *
 * This file contains the abstracted API client that services use.
 * Backend-specific implementation is isolated here.
 *
 * To switch backends, only modify this file and the interceptors.
 * To use mock data, set VITE_USE_MOCK_DATA=true in .env
 */

// Example: REST API implementation (Laravel/NestJS)
// For Supabase, replace with Supabase client implementation

import { env, getDefaultApiUrl } from "@/shared/config";
import { getAuthToken } from "@/shared/utils/auth";
import { useAuthStore } from "@/features/auth";
import { useImpersonationStore } from "@/features/user/model/impersonationStore";
import { mockApi } from "./mock";
import { getAcceptLanguageHeader } from "@/shared/utils/acceptLanguage";
import { SentryService, FrontendErrorCategory } from "@/core/sentry";

type ApiMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface ApiRequestConfig {
  method: ApiMethod;
  url: string;
  data?: unknown;
  params?: Record<string, string | number | boolean>;
  headers?: Record<string, string>;
}

interface ApiResponse<T = unknown, M = undefined> {
  data: T;
  status: number;
  meta?: M;
}

interface ApiError {
  message: string;
  code?: string;
  status?: number;
  errors?: Record<string, string[]>;
}

/** Error thrown by API client - extends Error so e.message works in catch blocks */
type ApiClientError = Error & ApiError;

class ApiClient {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  private async request<T>(config: ApiRequestConfig): Promise<ApiResponse<T>> {
    const url = new URL(this.baseURL + config.url);

    // Add query parameters (omit undefined/null/empty — avoids `text=undefined` in URLs)
    if (config.params) {
      Object.entries(config.params).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        if (value === "") return;
        url.searchParams.append(key, String(value));
      });
    }

    // Check if data is FormData
    const isFormData = config.data instanceof FormData;

    const headers: Record<string, string> = {};

    // Only set Content-Type for non-FormData requests
    // For FormData, let the browser set it with the proper boundary
    if (!isFormData) {
      headers["Content-Type"] = "application/json";
    }

    // Merge custom headers (but don't override Content-Type for FormData)
    if (config.headers) {
      Object.entries(config.headers).forEach(([key, value]) => {
        // Don't set Content-Type header for FormData - browser will set it with boundary
        if (!(isFormData && key.toLowerCase() === "content-type")) {
          headers[key] = value;
        }
      });
    }

    // Add auth token if available (read from Zustand store)
    const token = getAuthToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const impersonation = useImpersonationStore.getState();
    if (impersonation.isImpersonating() && impersonation.impersonatedContext?.id) {
      headers["X-Act-As-User-Id"] = impersonation.impersonatedContext.id;
    }

    headers["Accept-Language"] = getAcceptLanguageHeader();

    try {
      const response = await fetch(url.toString(), {
        method: config.method,
        headers,
        body: config.data
          ? isFormData
            ? (config.data as FormData)
            : JSON.stringify(config.data)
          : undefined,
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (response.status === 401 && token) {
          useAuthStore.getState().logout();
          window.location.replace("/auth");
        }

        const apiError = this.createError(data, response.status);

        if (SentryService.shouldCapture(response.status)) {
          const correlationId =
            response.headers.get("correlationId") ??
            ((data as Record<string, unknown>)?.correlationId as string | undefined);
          SentryService.captureException(apiError, {
            errorCategory: FrontendErrorCategory.ApiError,
            errorType: apiError.code ?? "ApiError",
            httpMethod: config.method,
            httpUrl: config.url,
            httpStatusCode: response.status,
            correlationId,
            apiType: "rest",
            level: SentryService.resolveLevel(response.status, FrontendErrorCategory.ApiError),
          });
        }

        throw apiError;
      }

      // Handle different response formats (Laravel: { data: [] }, NestJS: [], etc.)
      return {
        data: data.data ?? data,
        status: response.status,
        meta: data?.meta ?? undefined,
      };
    } catch (error: unknown) {
      if (error instanceof Error && "status" in error) {
        throw error;
      }

      const category = SentryService.classifyError(error);
      if (category === FrontendErrorCategory.NetworkError) {
        SentryService.captureException(error, {
          errorCategory: category,
          errorType: error instanceof Error ? error.name : "Unknown",
          httpMethod: config.method,
          httpUrl: config.url,
          apiType: "rest",
          level: SentryService.resolveLevel(undefined, category),
        });
      }

      const message = error instanceof Error ? error.message : String(error);
      const status =
        typeof error === "object" &&
        error !== null &&
        "status" in error &&
        typeof (error as { status: unknown }).status === "number"
          ? (error as { status: number }).status
          : 0;
      throw this.createError({ message }, status);
    }
  }

  private createError(data: unknown, status: number): ApiClientError {
    const error = data as Record<string, unknown>;
    const raw = error.message;
    const message =
      typeof raw === "string" ? raw : Array.isArray(raw) ? raw.join(" ") : "An error occurred";
    const err = new Error(message) as ApiClientError;
    err.status = status;
    err.code = (error.errorCode ?? error.code) as string;
    err.errors = error.errors as Record<string, string[]>;
    return err;
  }

  async get<T, M = undefined>(
    url: string,
    params?: Record<string, string | number | boolean>
  ): Promise<ApiResponse<T, M>> {
    return this.request<T>({
      method: "GET",
      url,
      params,
    }) as Promise<ApiResponse<T, M>>;
  }

  /**
   * GET request that returns a Blob (e.g. file download).
   * Use for export endpoints that return Excel/PDF etc.
   */
  async getBlob(
    url: string,
    params?: Record<string, string | number | boolean | undefined>
  ): Promise<Blob> {
    const fullUrl = new URL(this.baseURL + url);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== "") {
          fullUrl.searchParams.append(key, String(value));
        }
      });
    }
    const token = getAuthToken();
    const headers: Record<string, string> = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const impersonation = useImpersonationStore.getState();
    if (impersonation.isImpersonating() && impersonation.impersonatedContext?.id) {
      headers["X-Act-As-User-Id"] = impersonation.impersonatedContext.id;
    }
    headers["Accept-Language"] = getAcceptLanguageHeader();
    const response = await fetch(fullUrl.toString(), {
      method: "GET",
      headers,
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw this.createError(data as Record<string, unknown>, response.status);
    }
    return response.blob();
  }

  async post<T>(
    url: string,
    data?: unknown,
    config?: { headers?: Record<string, string> }
  ): Promise<ApiResponse<T>> {
    return this.request<T>({
      method: "POST",
      url,
      data,
      headers: config?.headers,
    });
  }

  async put<T>(url: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>({
      method: "PUT",
      url,
      data,
    });
  }

  async patch<T>(url: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>({
      method: "PATCH",
      url,
      data,
    });
  }

  async delete<T = void>(url: string): Promise<ApiResponse<T>> {
    return this.request<T>({
      method: "DELETE",
      url,
    });
  }
  /**
   * Post FormData for file uploads
   */
  async postFormData<T>(url: string, formData: FormData): Promise<T> {
    const fullUrl = new URL(this.baseURL + url);
    const token = getAuthToken();

    const headers: Record<string, string> = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    const impersonation = useImpersonationStore.getState();
    if (impersonation.isImpersonating() && impersonation.impersonatedContext?.id) {
      headers["X-Act-As-User-Id"] = impersonation.impersonatedContext.id;
    }
    headers["Accept-Language"] = getAcceptLanguageHeader();
    // Do not set Content-Type for FormData: the runtime must send
    // `multipart/form-data; boundary=...`. Setting only `multipart/form-data` breaks uploads.

    try {
      const response = await fetch(fullUrl.toString(), {
        method: "POST",
        headers,
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle 401 Unauthorized - automatically logout
        // Skip handling if already logging out, if this is the logout endpoint, or if this is the login endpoint
        // Login failures should not trigger automatic logout/redirect
        const isLogoutEndpoint = url.includes("auth/logout");
        const isLoginEndpoint = url.includes("auth/login");
        if (
          response.status === 401 &&
          // !this.isLoggingOut &&
          !isLogoutEndpoint &&
          !isLoginEndpoint
        ) {
          // TODO
          // await this.handleUnauthorized();
        }
        throw this.createError(data, response.status);
      }

      // Handle NestJS response format: { statusCode, message, data }
      return (data.data ?? data) as T;
    } catch (error) {
      if (error instanceof Error && "status" in error) {
        throw error;
      }
      throw this.createError({ message: "Network error" }, 0);
    }
  }
}

// Initialize API client
// Use mock API if VITE_USE_MOCK_DATA is enabled, otherwise use real API client
const apiBaseURL = (import.meta.env.VITE_API_URL as string | undefined) || getDefaultApiUrl();

// Export mock API when flag is enabled, otherwise export real API client
export const api = env.api.useMockData ? mockApi : new ApiClient(apiBaseURL);

// Export types for use in services
export type { ApiError, ApiResponse };
